import { Link } from 'react-router-dom';
import { Instagram, Facebook, Phone, Mail, MapPin, ArrowUp } from 'lucide-react';
import { CONTACT_INFO, SOCIAL_LINKS } from '@/lib/constants';
import { useConversionTracking } from '@/hooks/useMarketingTracking';

export default function Footer() {
  const { trackPhoneCall } = useConversionTracking();
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-brand-black text-white pt-32 pb-12 px-6 lg:px-12 overflow-hidden relative">
      {/* Background Decorative Text */}
      <div className="absolute -bottom-20 -right-20 pointer-events-none opacity-[0.03] select-none">
        <span className="text-[25vw] font-display font-bold leading-none">DELTA</span>
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 mb-32">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-4 mb-8 group">
              <div className="w-16 h-16 flex items-center justify-center p-1">
                <img 
                  src="/images/delta-logo.svg" 
                  alt="Delta BJJ" 
                  className="w-full h-full transform group-hover:rotate-[360deg] transition-transform duration-1000"
                  style={{ 
                    filter: 'invert(21%) sepia(82%) saturate(1966%) hue-rotate(205deg) brightness(91%) contrast(92%)',
                    transformOrigin: '50% 70%'
                  }}
                />
              </div>
              <div className="flex flex-col">
                <span className="text-white font-display font-extrabold text-3xl md:text-4xl tracking-tighter leading-none">DELTA</span>
                <span className="text-white font-display text-sm md:text-base tracking-[0.3em] leading-none mt-1 uppercase font-extrabold">JIU-JITSU</span>
              </div>
            </Link>
            <p className="text-white text-base md:text-lg leading-relaxed mb-10 max-w-sm font-bold">
              Delta BJJ has been elevating the art of Brazilian Jiu-Jitsu in Culver City since 1998. Join our legacy and community to build discipline, excellence, and resilience on the mats and off.
            </p>
            <div className="flex gap-6">
              {[
                { icon: Instagram, href: SOCIAL_LINKS.instagram },
                { icon: Facebook, href: SOCIAL_LINKS.facebook },
              ].map((social, i) => (
                <a 
                  key={i} 
                  href={social.href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center hover:bg-brand-blue hover:border-brand-blue hover:text-white transition-all"
                >
                  <social.icon className="w-6 h-6" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-brand-blue font-display text-sm md:text-base tracking-[0.4em] uppercase mb-10 font-extrabold">Explore</h4>
            <ul className="space-y-6">
              {[
                { name: 'Programs', href: '/brazilian-jiu-jitsu-training-programs' },
                { name: 'Schedule', href: '/schedule' },
                { name: 'Instructors', href: '/instructors' },
                { name: 'About Delta BJJ', href: '/about-delta-bjj-culver-city' },
                { name: 'Contact', href: '/contact-bjj-culver-city' },
              ].map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-base md:text-lg font-extrabold uppercase tracking-widest text-white hover:text-brand-blue transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info & Map */}
          <div className="lg:col-span-6">
            <h4 className="text-brand-blue font-display text-sm md:text-base tracking-[0.4em] uppercase mb-10 font-extrabold">Get in Touch</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-8">
              <div className="space-y-8">
                <a 
                  href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} 
                  onClick={trackPhoneCall}
                  className="flex items-center gap-4 group"
                >
                  <div className="w-14 h-14 rounded-sm bg-white/5 flex items-center justify-center group-hover:bg-brand-blue transition-colors">
                    <Phone className="w-6 h-6 text-brand-blue group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <span className="block text-xs md:text-sm text-white uppercase tracking-widest mb-1 font-extrabold">Call Us</span>
                    <span className="block text-base md:text-lg font-extrabold text-white">{CONTACT_INFO.phone}</span>
                  </div>
                </a>
                <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-4 group">
                  <div className="w-14 h-14 rounded-sm bg-white/5 flex items-center justify-center group-hover:bg-brand-blue transition-colors">
                    <Mail className="w-6 h-6 text-brand-blue group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <span className="block text-xs md:text-sm text-white uppercase tracking-widest mb-1 font-extrabold">Email Us</span>
                    <span className="block text-base md:text-lg font-extrabold text-white">{CONTACT_INFO.email}</span>
                  </div>
                </a>
              </div>
              <div className="space-y-8">
                <div>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-sm bg-white/5 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-brand-blue" />
                    </div>
                    <div>
                      <span className="block text-xs md:text-sm text-white uppercase tracking-widest mb-1 font-extrabold">Location</span>
                      <span className="block text-base md:text-lg font-extrabold text-white leading-relaxed">
                        {CONTACT_INFO.address}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 relative aspect-video overflow-hidden rounded-sm border border-white/10">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3306.5!2d-118.395!3d34.0123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2b9b8c8c8c8c9%3A0x0!2zMTEwMjQgV2FzaGluZ3RvbiBCbHZkLCBDdWx2ZXIgQ2l0eSwgQ0EgOTAyMzI!5e0!3m2!1sen!2sus!4v1234567890!5m2!1sen!2sus"
                      width="100%"
                      height="100%"
                      style={{ border: 0, filter: 'grayscale(1) contrast(1.2)' }}
                      allowFullScreen
                      loading="lazy"
                      title="Delta BJJ Location"
                      className="absolute inset-0"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-12 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-8">
          <p className="text-sm md:text-base tracking-widest uppercase font-extrabold text-white">
            © {currentYear} DELTA JIU-JITSU. ALL RIGHTS RESERVED.
          </p>
          
          <button 
            onClick={scrollToTop}
            className="group flex items-center gap-4 text-sm md:text-base tracking-widest uppercase font-extrabold text-white hover:text-brand-blue transition-colors"
          >
            Back to Top
            <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-brand-blue group-hover:border-brand-blue group-hover:text-white transition-all">
              <ArrowUp className="w-5 h-5" />
            </div>
          </button>
        </div>
      </div>
    </footer>
  );
}
